/**
 * usePageTrail — the breadcrumb trail for the current admin page.
 *
 * Reads the leaf route match, walks up the `parent` links of the page trail
 * map, and puts the tenant first (linking to /admin). The result is the
 * `items` prop of the library Breadcrumb / the `crumbs` prop of PageHeader:
 *
 *   <PageHeader crumbs={usePageTrail()} icon={usePageIcon()} title="Users" />
 *
 * Pages with a dynamic label (a tenant's display name, a walkthrough's title)
 * or a crumb that should carry search params override it by route id:
 *
 *   usePageTrail({ '/admin/platform/tenants/$tenantId': { label: tenant.name } })
 */

import { useMatches } from '@tanstack/react-router';
import { useAdminAuth } from '../hooks/useAdminAuth';
import type { AdminUser } from '../types/admin';
import type { LucideIcon } from 'lucide-react';
import { ADMIN_INDEX_ROUTE_ID, PAGE_TRAIL, pageTrailIcon, trailPath, type PageTrailEntry } from './pageTrail';

/** One crumb: the shape the library Breadcrumb's `items` prop takes. */
export interface PageTrailCrumb {
  label: string;
  to?: string;
  params?: Record<string, string>;
  search?: Record<string, unknown>;
}

export interface PageTrailOverride {
  label?: string;
  params?: Record<string, string>;
  search?: Record<string, unknown>;
}

/** Per-crumb overrides, keyed by the crumb's route id. */
export type PageTrailOverrides = Partial<Record<string, PageTrailOverride>>;

export interface PageTrailLeaf {
  routeId: string;
  search: Record<string, unknown>;
  params: Record<string, string>;
}

/** The tenant crumb's label: the same fallback the sidebar's account card uses. */
export function tenantLabel(user: Pick<AdminUser, 'tenantName' | 'tenantSlug'> | null | undefined): string | null {
  return user?.tenantName ?? user?.tenantSlug ?? null;
}

/** The leaf page's own label (search-dependent variants applied), or null for an unmapped route. */
export function pageTrailLabel(leaf: Pick<PageTrailLeaf, 'routeId' | 'search'>): string | null {
  const entry = PAGE_TRAIL[leaf.routeId];
  if (!entry) return null;
  return entry.fromSearch?.(leaf.search).label ?? entry.label;
}

/** Route ids from the leaf up to (not including) the tenant, leaf first. */
function trailRouteIds(leaf: PageTrailLeaf): string[] {
  const ids: string[] = [];
  let id: string | undefined = leaf.routeId;
  // The map is a tree, but cap the walk so a bad edit can never hang a render.
  while (id && id !== ADMIN_INDEX_ROUTE_ID && PAGE_TRAIL[id] && ids.length < 10) {
    ids.push(id);
    const entry: PageTrailEntry = PAGE_TRAIL[id]!;
    id = entry.fromSearch?.(leaf.search).parent ?? entry.parent;
  }
  return ids;
}

/** Pure trail builder behind `usePageTrail`. */
export function buildPageTrail(
  leaf: PageTrailLeaf,
  tenant: string | null,
  overrides: PageTrailOverrides = {},
): PageTrailCrumb[] {
  const crumbs: PageTrailCrumb[] = [{ label: tenant ?? 'Switchboard', to: '/admin' }];
  const ids = trailRouteIds(leaf).reverse();
  ids.forEach((id, i) => {
    const override = overrides[id] ?? {};
    const isLeaf = i === ids.length - 1;
    const label = override.label ?? (isLeaf ? pageTrailLabel(leaf) : null) ?? PAGE_TRAIL[id]!.label;
    if (isLeaf) {
      // The current page is never a link.
      crumbs.push({ label });
      return;
    }
    const crumb: PageTrailCrumb = { label, to: trailPath(id) };
    const params = override.params ?? (id.includes('$') ? leaf.params : undefined);
    if (params) crumb.params = params;
    if (override.search) crumb.search = override.search;
    crumbs.push(crumb);
  });
  return crumbs;
}

function useLeaf(): PageTrailLeaf {
  const matches = useMatches();
  const leafMatch = matches[matches.length - 1];
  return {
    routeId: leafMatch?.routeId ?? ADMIN_INDEX_ROUTE_ID,
    search: (leafMatch?.search ?? {}) as Record<string, unknown>,
    params: (leafMatch?.params ?? {}) as Record<string, string>,
  };
}

export function usePageTrail(overrides?: PageTrailOverrides): PageTrailCrumb[] {
  const leaf = useLeaf();
  const { user } = useAdminAuth();
  return buildPageTrail(leaf, tenantLabel(user), overrides);
}

/**
 * The current page's header icon — the same icon as its sidebar entry — for
 * PageHeader's `icon` prop:
 *
 *   <PageHeader crumbs={usePageTrail()} icon={usePageIcon()} title="Users" />
 */
export function usePageIcon(): LucideIcon | undefined {
  const leaf = useLeaf();
  return pageTrailIcon(leaf.routeId, leaf.search);
}
